import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { USE_CASES, setPageMeta } from "../data/useCases";
import { getSolutionPage } from "../data/solutionPages";
import { ArrowRight, Gift, Info } from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

export default function UseCasesIndex() {
  useEffect(() => {
    setPageMeta(
      "Cas d'usage | Découvrez Junago en situation",
      "Découvrez comment Junago peut accompagner les loueurs dans la gestion des réservations, de la flotte, des départs, des retours et des paiements."
    );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-14" data-testid="use-cases-hero">
        <div className={`${OVERLINE} mb-4`}>Cas d'usage</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-slate-900 max-w-3xl" data-testid="use-cases-title">
          Découvrez Junago en situation
        </h1>
        <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Explorez des situations concrètes et découvrez comment Junago accompagne les professionnels
          de la location à chaque étape de leur activité.
        </p>
        <p className="mt-5 flex items-start gap-2 text-xs text-slate-400 max-w-2xl" data-testid="use-cases-disclaimer">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Les cas présentés sont des exemples illustratifs destinés à montrer les possibilités
          d'utilisation de Junago. Ils ne constituent pas des témoignages de clients réels.
        </p>
      </section>

      {/* Grid */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="use-cases-grid">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {USE_CASES.map((c) => {
              const sol = getSolutionPage(c.solutionSlug);
              return (
                <Link
                  key={c.slug}
                  to={`/cas-d-usage/${c.slug}`}
                  className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-[0_16px_40px_-16px_rgba(124,58,237,0.3)] transition-all flex flex-col"
                  data-testid={`use-case-card-${c.slug}`}
                >
                  <div className="h-44 overflow-hidden shrink-0">
                    <img src={c.heroImg} alt={c.heroAlt} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    {sol && (
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.1em] uppercase text-violet-600">
                        <sol.icon className="h-3.5 w-3.5" /> {sol.menuLabel}
                      </div>
                    )}
                    <h2 className="mt-2 font-heading text-base font-medium text-slate-900 leading-snug group-hover:text-violet-700 transition-colors">
                      {c.title}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{c.excerpt}</p>
                    <div className="mt-auto pt-4 flex items-center gap-2.5">
                      <span className="h-8 w-8 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
                        <c.profile.icon className="h-3.5 w-3.5 text-violet-600" />
                      </span>
                      <div className="text-xs">
                        <div className="font-semibold text-slate-800">{c.profile.label}</div>
                        <div className="text-slate-500">{c.profile.detail}</div>
                      </div>
                      <ArrowRight className="ml-auto h-4 w-4 text-violet-500 transition-transform group-hover:translate-x-1 shrink-0" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20" data-testid="use-cases-cta">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
          <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">Passez de l'exemple à la pratique.</h2>
            <p className="mt-3 text-slate-400">30 jours d'essai complet, sans carte bancaire.</p>
            <Link to="/register?context=vendor" className="btn-jn-light mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold" data-testid="use-cases-bottom-start">
              <Gift className="h-4 w-4" /> Commencer gratuitement
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
